import React, { useState } from 'react';
import { useDrag } from 'react-dnd';
import { MdClose } from 'react-icons/md';

const DraggableControlCard = ({ control, index, onRemove, framework, onAssign }) => {
    const [{ isDragging }, dragRef] = useDrag(() => ({
        type: 'CONTROL',
        item: () => ({ index, control, id: control.id }),
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging(),
        }),
    }));

    const COMPLIANCE_STYLES = {
        NIST: "bg-blue-100 text-blue-800 border-blue-300",
        COBIT: "bg-purple-100 text-purple-800 border-purple-300",
        ISO27001: "bg-green-100 text-green-800 border-green-300",
        GDPR: "bg-orange-100 text-orange-800 border-orange-300",
    };

    const [isHovered, setIsHovered] = useState(false);
    const [showSOA, setShowSOA] = useState(false);
    const [soaDraft, setSoaDraft] = useState({
        soa_applicability_status: 'Applicable',
        soa_implementation_status: '',
        soa_implementation_date: '',
        soa_justification: '',
    });

    const handleMouseDown = (e) => {
        e.stopPropagation();
    };

    const handleAssign = (e) => {
        e.stopPropagation();
        if (showSOA) {
            onAssign && onAssign(control.iso_control_id, soaDraft);
            setShowSOA(false);
        } else {
            setShowSOA(true);
        }
    };

    return (
        <div
            ref={dragRef}
            onMouseDown={handleMouseDown}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={`relative control-card shadow p-3 mb-2 rounded bg-white bg-opacity-70 backdrop-blur-md border border-gray-300 transition-all duration-300 ease-in-out ${
                isDragging ? 'opacity-50' : ''
            }`}
            style={{
                transform: `translateY(${index * 10}px)`,
                boxShadow: isHovered ? '0px 8px 20px rgba(0, 0, 0, 0.3)' : 'none',
                cursor: 'pointer',
                opacity: isHovered ? 1 : 0.9,
                transition: 'all 0.3s ease',
            }}
        >
            <MdClose
                className="absolute top-1 right-1 text-gray-500 hover:text-red-600 cursor-pointer"
                size={20}
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove(control.id);
                }}
            />

            <h4 className="font-bold pr-5">{control.title}</h4>
            <p className="text-xs text-gray-500">Type: {control.type}</p>
            <p className="text-xs text-gray-600 mt-1">{control.description}</p>

            {Array.isArray(control.complianceTypes) && control.complianceTypes.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                    {[...new Set(control.complianceTypes)]
                        .filter((fw) => framework === 1 ? true : fw !== "NIST" && fw !== "COBIT" && fw !== "ISO27001")
                        .map((fw) => (
                            <span key={fw}
                                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${COMPLIANCE_STYLES[fw] || "bg-gray-100 text-gray-700 border-gray-300"}`}>
                                {fw}
                            </span>
                        ))}
                </div>
            )}

            {/* SOA form (shown inline when Assign is clicked) */}
            {showSOA && (
                <div className="mt-3 border-t border-gray-200 pt-3 flex flex-col gap-2" onClick={e => e.stopPropagation()}>
                    <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-medium text-gray-600">Applicability</label>
                        <select value={soaDraft.soa_applicability_status}
                            onChange={e => setSoaDraft(d => ({ ...d, soa_applicability_status: e.target.value }))}
                            className="h-8 px-2 rounded border border-gray-200 text-xs bg-white focus:outline-none">
                            {["Applicable","Partially Applicable","Not Applicable"].map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-medium text-gray-600">Implementation</label>
                        <select value={soaDraft.soa_implementation_status}
                            onChange={e => setSoaDraft(d => ({ ...d, soa_implementation_status: e.target.value }))}
                            className="h-8 px-2 rounded border border-gray-200 text-xs bg-white focus:outline-none">
                            <option value="">— Select —</option>
                            {["Implemented","Partially Implemented","Not Implemented","Planned"].map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-medium text-gray-600">Justification</label>
                        <textarea value={soaDraft.soa_justification}
                            onChange={e => setSoaDraft(d => ({ ...d, soa_justification: e.target.value }))}
                            rows={2} placeholder="Optional…"
                            className="px-2 py-1.5 rounded border border-gray-200 text-xs focus:outline-none resize-none" />
                    </div>
                    <div className="flex gap-2 mt-1">
                        <button onClick={e => { e.stopPropagation(); setShowSOA(false); }}
                            className="flex-1 h-7 rounded border border-gray-200 text-xs text-gray-600 hover:bg-gray-50">
                            Cancel
                        </button>
                        <button onClick={handleAssign}
                            className="flex-1 h-7 rounded bg-[#1a7a4a] text-white text-xs font-medium hover:opacity-90">
                            Confirm
                        </button>
                    </div>
                </div>
            )}

            {/* Assign button — only show if iso_control_id is present and SOA form not open */}
            {!showSOA && control.iso_control_id && onAssign && (
                <button onClick={handleAssign}
                    className="mt-2 w-full h-7 rounded bg-[#1a7a4a]/10 border border-[#1a7a4a]/20 text-[11px] font-medium text-[#1a7a4a] hover:bg-[#1a7a4a]/20 transition">
                    Assign to Risk
                </button>
            )}
        </div>
    );
};

export default DraggableControlCard;
